import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Check, Palette, FileText, Save, Settings2 } from "lucide-react";

const categoryColors = {
  Classique: "bg-blue-50 text-blue-600",
  Moderne: "bg-cyan-50 text-cyan-600",
  Corporate: "bg-slate-100 text-slate-600",
  Elegant: "bg-amber-50 text-amber-600",
  Professionnel: "bg-emerald-50 text-emerald-600",
  Industriel: "bg-orange-50 text-orange-600",
  Minimaliste: "bg-gray-50 text-gray-600",
  Nature: "bg-green-50 text-green-600",
  Luxe: "bg-yellow-50 text-yellow-700",
  Audacieux: "bg-red-50 text-red-600",
};

export default function TemplatesPage() {
  const { apiFetch, API } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState("classic_blue");
  const [customOverrides, setCustomOverrides] = useState({});
  const [mentionsLegales, setMentionsLegales] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoSize, setLogoSize] = useState(75);
  const [headerHeight, setHeaderHeight] = useState(65);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/templates`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates);
        setActiveId(data.active_template_id);
        setCustomOverrides(data.custom_overrides || {});
        setMentionsLegales(data.mentions_legales || "");
        setLogoSize(data.custom_overrides?.logo_size || data.templates.find(t => t.template_id === data.active_template_id)?.logo_size || 75);
        setHeaderHeight(data.custom_overrides?.header_height || data.templates.find(t => t.template_id === data.active_template_id)?.header_height || 65);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiFetch, API]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const selectTemplate = (templateId) => {
    setActiveId(templateId);
    const tmpl = templates.find(t => t.template_id === templateId);
    if (tmpl) {
      setLogoSize(customOverrides.logo_size || tmpl.logo_size);
      setHeaderHeight(customOverrides.header_height || tmpl.header_height);
    }
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`${API}/templates/active`, {
        method: "PUT",
        body: JSON.stringify({
          template_id: activeId,
          custom_overrides: { logo_size: logoSize, header_height: headerHeight },
          mentions_legales: mentionsLegales,
        }),
      });
      if (res.ok) {
        toast.success("Modèle enregistré avec succès");
        fetchTemplates();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur de sauvegarde"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-64" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-40 bg-slate-200 rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  const activeTemplate = templates.find(t => t.template_id === activeId);

  return (
    <div className="p-4 sm:p-8" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h1 className="font-barlow text-xl sm:text-2xl font-bold text-slate-900 tracking-tight uppercase">Modèles de Facture</h1>
          <p className="text-sm text-slate-500 mt-1">Choisissez le design de vos documents PDF</p>
        </div>
        <Button onClick={saveTemplate} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="save-template-btn">
          <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template grid */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {templates.map(t => {
              const isActive = t.template_id === activeId;
              const [r, g, b] = t.header_color;
              const catColor = categoryColors[t.category] || "bg-slate-100 text-slate-600";
              return (
                <Card
                  key={t.template_id}
                  className={`cursor-pointer transition-all hover:shadow-md border-2 ${isActive ? "border-orange-500 shadow-lg" : "border-slate-200 hover:border-slate-300"}`}
                  onClick={() => selectTemplate(t.template_id)}
                  data-testid={`template-${t.template_id}`}
                >
                  <CardContent className="p-0">
                    {/* Mini preview */}
                    <div className="relative">
                      <div
                        className="h-16 rounded-t-lg flex items-center justify-between px-3"
                        style={{ backgroundColor: `rgb(${r},${g},${b})` }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-white/90 rounded" />
                          <div className="space-y-1">
                            <div className="h-1.5 w-10 bg-white/60 rounded" />
                            <div className="h-1 w-8 bg-white/40 rounded" />
                          </div>
                        </div>
                        <div className="text-right space-y-1">
                          <div className="h-2 w-12 bg-white/60 rounded" />
                          <div className="h-1 w-8 bg-white/40 rounded ml-auto" />
                        </div>
                      </div>
                      {/* Table preview */}
                      <div className="px-3 pt-2 pb-3">
                        <div className="h-1.5 w-full rounded mb-1" style={{ backgroundColor: `rgb(${r},${g},${b})`, opacity: 0.2 }} />
                        <div className="space-y-1">
                          <div className="h-1 w-full bg-slate-100 rounded" />
                          <div className="h-1 w-full bg-slate-50 rounded" />
                          <div className="h-1 w-full bg-slate-100 rounded" />
                        </div>
                        <div className="mt-2 h-2 w-16 rounded ml-auto" style={{ backgroundColor: `rgb(${r},${g},${b})` }} />
                      </div>
                      {isActive && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <Separator />
                    <div className="p-2.5">
                      <p className="text-xs font-medium text-slate-800 truncate">{t.name}</p>
                      <Badge className={`${catColor} text-[9px] border-0 mt-1 px-1.5 py-0`}>{t.category}</Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Settings panel */}
        <div className="space-y-4">
          {/* Active template preview */}
          {activeTemplate && (
            <Card className="border-orange-200 bg-orange-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Palette className="w-4 h-4 text-orange-600" />
                  <h3 className="text-sm font-semibold text-slate-900">Modèle actif</h3>
                </div>
                <p className="text-sm font-medium text-orange-700">{activeTemplate.name}</p>
                <Badge className={`${categoryColors[activeTemplate.category]} text-[10px] border-0 mt-1`}>{activeTemplate.category}</Badge>
              </CardContent>
            </Card>
          )}

          {/* Logo & Header settings */}
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-5">
              <div className="flex items-center gap-2 mb-1">
                <Settings2 className="w-4 h-4 text-slate-600" />
                <h3 className="text-sm font-semibold text-slate-900">Personnalisation</h3>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Taille du logo</Label>
                  <span className="text-xs text-slate-500 font-mono">{logoSize}mm</span>
                </div>
                <Slider
                  value={[logoSize]}
                  onValueChange={([v]) => setLogoSize(v)}
                  min={30}
                  max={100}
                  step={5}
                  data-testid="logo-size-slider"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Hauteur en-tête</Label>
                  <span className="text-xs text-slate-500 font-mono">{headerHeight}mm</span>
                </div>
                <Slider
                  value={[headerHeight]}
                  onValueChange={([v]) => setHeaderHeight(v)}
                  min={40}
                  max={90}
                  step={5}
                  data-testid="header-height-slider"
                />
              </div>
            </CardContent>
          </Card>

          {/* Legal mentions */}
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-600" />
                <h3 className="text-sm font-semibold text-slate-900">Mentions légales</h3>
              </div>
              <Textarea
                value={mentionsLegales}
                onChange={e => setMentionsLegales(e.target.value)}
                rows={6}
                className="text-xs"
                placeholder="Conditions de paiement, TVA..."
                data-testid="mentions-legales-input"
              />
              <p className="text-[10px] text-slate-400">Ces mentions apparaîtront en bas de chaque facture PDF.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
